import { ProductClassification } from '~/server/types';
import { useAuth } from './useAuth';
import type { DealerContent } from '~/server/types';

export const DealerContentStorageKey = 'dealer-content';

export const useDealerContent = () => {
  const { userData } = useAuth();
  const dealerContent = useState<DealerContent | null>('dealerContent', () => null);
  const isLoading = ref(false);
  const error = ref<Error | null>(null);

  const getProductClassificationFromName = (name: string): ProductClassification | undefined => {
    switch (name) {
      case 'wholesale':
        return ProductClassification.Wholesale;
      case 'madeToMeasure':
        return ProductClassification.MadeToMeasure;
      case 'spares':
        return ProductClassification.Spares;
      case 'samples':
        return ProductClassification.Samples;
      case 'marketingMaterials':
        return ProductClassification.Materials;
    }
  };

  const getDealerContentKey = () => {
    return userData.value?.dealers?.[0]?.dealerContentKey;
  };

  const getDealerContentFromSessionStorage = () => {
    if (!import.meta.client) return null;

    try {
      const storedContent = sessionStorage.getItem(DealerContentStorageKey);
      if (!storedContent) return null;
      return JSON.parse(storedContent);
    } catch (err) {
      console.error('Error parsing stored dealer content:', err);
      return null;
    }
  };

  const fetchDealerContentFromApi = async (dealerContentKey: string) => {
    try {
      const data = await $fetch(`/api/content`, {
        method: 'GET',
        params: {
          path: dealerContentKey,
          includeAncestors: false
        },
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store'
        }
      });

      return {
        ...(data.properties as any),
        productTypeExclusions: (data.properties as any)?.productType // remap productType to productTypeExclusions
      } as DealerContent;
    } catch (e) {
      console.error('Exception in fetchDealerContentFromApi:', e);
      return null;
    }
  };

  const fetchDealerContent = async (forceRefresh = false) => {
    if (dealerContent.value && !forceRefresh) {
      return dealerContent.value;
    }

    if (!forceRefresh && import.meta.client) {
      const storedContent = getDealerContentFromSessionStorage();
      if (storedContent) {
        dealerContent.value = storedContent;
        return dealerContent.value;
      }
    }

    const dealerContentKey = getDealerContentKey();
    if (!dealerContentKey) {
      error.value = new Error('No dealer content key found in user data');
      return null;
    }

    isLoading.value = true;
    error.value = null;

    try {
      const dealerContentFromApi = await fetchDealerContentFromApi(dealerContentKey);
      dealerContent.value = dealerContentFromApi ?? null;

      if (import.meta.client && dealerContent.value) {
        sessionStorage.setItem(DealerContentStorageKey, JSON.stringify(dealerContent.value));
      }

      return dealerContentFromApi;
    } catch (err) {
      console.error('Error fetching dealer content:', err);
      error.value = err instanceof Error ? err : new Error('Unknown error fetching dealer content');
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const isProductClassificationExcluded = (classification: ProductClassification) => {
    if (!dealerContent.value) return false;

    return (
      dealerContent.value?.productClassificationExclusions
        ?.map(getProductClassificationFromName)
        .includes(classification) ?? false
    );
  };

  const isProductTypeExcluded = (type: string) => {
    if (!dealerContent.value) return false;

    return dealerContent.value?.productTypeExclusions?.includes(type) ?? false;
  };

  const isProductExcluded = (sku: string) => {
    if (!dealerContent.value) return false;

    const excludedProducts = dealerContent.value?.excludedProducts;
    if (!excludedProducts) return false;

    const excludedList = excludedProducts.split(',').map(p => p.trim());
    return excludedList.includes(sku);
  };

  const isWholesaleSKUExcluded = (sku: string) => {
    if (!dealerContent.value) return false;

    const excludedSKUs = dealerContent.value?.excludedWholesaleSKUs;
    if (!excludedSKUs) return false;

    const excludedList = excludedSKUs.split(',').map(p => p.trim());
    return excludedList.includes(sku);
  };

  const isRangeExcluded = (range: string) => {
    if (!dealerContent.value) return false;

    const excludedRanges = dealerContent.value?.excludedRanges;
    if (!excludedRanges) return false;

    const excludedList = excludedRanges.split(',').map(p => p.trim());
    return excludedList.includes(range);
  };

  /**
   * Generates Algolia exclusion filters based on dealer content
   * @returns A string containing all the exclusion filters for Algolia
   */
  const getAlgoliaExclusionFilters = () => {
    const accessControlFilters: string[] = [];

    // Exclude products by product type if specified in dealer content
    if (dealerContent.value?.productTypeExclusions?.length) {
      const excludedProductTypes = dealerContent.value.productTypeExclusions
        .map(type => `NOT productType:${type}`)
        .join(' AND ');
      if (excludedProductTypes) {
        accessControlFilters.push(`(${excludedProductTypes})`);
      }
    }

    // Exclude products by classification if specified in dealer content
    if (dealerContent.value?.productClassificationExclusions?.length) {
      const excludedClassifications = dealerContent.value.productClassificationExclusions
        .map(getProductClassificationFromName)
        .map(classification => `NOT classification:${classification}`)
        .join(' AND ');
      if (excludedClassifications) {
        accessControlFilters.push(`(${excludedClassifications})`);
      }
    }

    // Exclude specific SKUs if specified in dealer content
    if (dealerContent.value?.excludedProducts) {
      const excludedProducts = dealerContent.value.excludedProducts
        .split(',')
        .map(sku => sku.trim())
        .filter(Boolean)
        .map(sku => `NOT sku:"${sku}"`)
        .join(' AND ');

      if (excludedProducts) {
        accessControlFilters.push(`(${excludedProducts})`);
      }
    }

    // Exclude wholesale SKUs if specified in dealer content
    if (dealerContent.value?.excludedWholesaleSKUs && dealerContent.value.excludedWholesaleSKUs.trim()) {
      const excludedWholesaleSKUs = dealerContent.value.excludedWholesaleSKUs
        .split(',')
        .map(sku => sku.trim())
        .filter(Boolean)
        .map(sku => `NOT sku:"${sku}"`)
        .join(' AND ');

      if (excludedWholesaleSKUs) {
        accessControlFilters.push(`(${excludedWholesaleSKUs})`);
      }
    }

    // Exclude ranges if specified in dealer content
    if (dealerContent.value?.excludedRanges && dealerContent.value.excludedRanges.trim()) {
      const excludedRanges = dealerContent.value.excludedRanges
        .split(',')
        .map(range => range.trim())
        .filter(Boolean)
        .map(range => `NOT patterns:"${range}"`)
        .join(' AND ');

      if (excludedRanges) {
        accessControlFilters.push(`(${excludedRanges})`);
      }
    }

    return accessControlFilters.join(' AND ');
  };

  const clearDealerContent = () => {
    dealerContent.value = null;
    if (import.meta.client) {
      sessionStorage.removeItem(DealerContentStorageKey);
    }
  };

  // Initialize dealer content on both client and server
  if (import.meta.client) {
    onMounted(() => {
      fetchDealerContent();
    });
  } else {
    // For server-side, fetch immediately if we have user data
    if (userData.value?.dealers?.[0]?.dealerContentKey) {
      fetchDealerContent();
    }
  }

  return {
    dealerContent: computed(() => dealerContent.value),
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),
    fetchDealerContent,
    isProductClassificationExcluded,
    isProductTypeExcluded,
    isProductExcluded,
    isWholesaleSKUExcluded,
    isRangeExcluded,
    getAlgoliaExclusionFilters,
    clearDealerContent
  };
};
